import * as React from "react";

import {
  CanonicalDataType,
  MessagePathNode,
  MessagePathPartType,
  TopicNode,
} from "@/shared/domain/topics";
import { type Message } from "@/shared/reader";
import { useMcapReader } from "@/shared/reader/mcap/useMcapReader";
import { ErrorMonitoringService } from "@/shared/services";
import {
  LayoutItem,
  RawMessagePanelState,
  TopicData,
} from "@/shared/state/visualization";
import { NS_PER_S_BIGINT } from "@/shared/time";

import { RawMessagePanelSettingsSidebarProps } from "../../PanelSettings/RawMessagePanelSettings";
import { SidebarViewType, useWorkspaceSidebar } from "../../sidebarContext";
import { useWorkspaceTimer } from "../../WorkspaceCtx";
import { useSelectPanel, useTopicTreeForFile } from "../../WorkspaceCtx/hooks";
import { DataNotFoundMessage } from "../DataNotFoundMessage";
import { NoDataMessage } from "../NoDataMessage";
import { PanelLayout } from "../PanelLayout";

import { FormattedMessage } from "./FormattedMessage";

interface RawMessagePanelProps {
  layout: LayoutItem;
  state: RawMessagePanelState;
}

export function RawMessagePanel({ layout, state }: RawMessagePanelProps) {
  const timer = useWorkspaceTimer();
  const sidebar = useWorkspaceSidebar();
  const selectPanel = useSelectPanel();
  const topicTree = useTopicTreeForFile(
    state.data?.topic.association.association_id,
  );

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<Error | null>(null);
  const [message, setMessage] = React.useState<Message | null>(null);

  const mcapReader = useMcapReader({
    fileId: state.data?.representation.association.association_id,
    onError: setError,
    onLoading: setIsLoading,
  });

  React.useEffect(
    function validateTopicData() {
      if (!state.data) {
        return;
      }

      if (!isViewableInRawMessagePanel(topicTree, state.data)) {
        setError(
          new Error(
            `Topic "${state.data.topic.name}" contains binary data that isn’t currently viewable in the Raw Panel. We're actively working to remove this limitation.`,
          ),
        );
      }
    },
    [state.data, topicTree],
  );

  const abortControllerRef = React.useRef(new AbortController());
  const timerHoldRef = React.useRef<symbol | null>(null);
  const updateMessage = React.useCallback(
    function update() {
      if (mcapReader === null) {
        return;
      }

      if (error !== null) {
        return;
      }

      const currentTime = timer.currentTime;
      if (currentTime < (mcapReader.startTime ?? 0n)) {
        setMessage(null);
        return;
      }

      // Attempt to get the latest message synchronously
      const message = mcapReader.getLatestMessageAsOfTime(currentTime);
      if (message) {
        setMessage(message);
        return;
      }

      // Otherwise, load the message asynchronously
      abortControllerRef.current.abort();
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      if (timerHoldRef.current === null) {
        timerHoldRef.current = timer.hold();
      }
      setIsLoading(true);
      mcapReader
        .loadMessageAsOfTime(currentTime, {
          bufferDurationNs: 10n * NS_PER_S_BIGINT, // And load about 10 seconds of messages into the future
        })
        .catch((err) => {
          if (abortController.signal.aborted) {
            return;
          }
          const error =
            err instanceof Error
              ? err
              : new Error("Failed to get latest message for RawMessagePanel", {
                  cause: err,
                });
          setError(error);
        })
        .finally(() => {
          if (abortController.signal.aborted) {
            return;
          }
          // Get the latest message again after loading.
          const message = mcapReader.getLatestMessageAsOfTime(currentTime);
          if (message) {
            setMessage(message);
            setError(null);
          }

          setIsLoading(false);
          if (timerHoldRef.current !== null) {
            timer.holdRelease(timerHoldRef.current);
            timerHoldRef.current = null;
          }
        });
    },
    [error, mcapReader, timer],
  );

  React.useEffect(
    function setTimerCallbacks() {
      const abortController = new AbortController();

      timer.addListener("tick", updateMessage, {
        signal: abortController.signal,
      });
      timer.addListener("seek", updateMessage, {
        signal: abortController.signal,
      });
      timer.addListener("start", updateMessage, {
        signal: abortController.signal,
      });
      timer.addListener("stop", updateMessage, {
        signal: abortController.signal,
      });

      updateMessage();

      return function abort() {
        abortController.abort();
        if (timerHoldRef.current !== null) {
          timer.holdRelease(timerHoldRef.current);
          timerHoldRef.current = null;
        }
      };
    },
    [timer, updateMessage],
  );

  /**
   * Report errors when they occur
   */
  React.useEffect(
    function reportError() {
      if (error === null) {
        return;
      }
      ErrorMonitoringService.captureError(error);
    },
    [error],
  );

  return (
    <PanelLayout isLoading={isLoading} state={state} layout={layout}>
      <NoDataMessage
        panelData={state.data}
        onAddDataAction={() => {
          // Open the sidebar and spotlight the topic field
          selectPanel(state.id);
          sidebar.openView<RawMessagePanelSettingsSidebarProps>({
            type: SidebarViewType.PanelSettings,
            data: {
              field: "Topic",
              spotlightKey: performance.now(),
            },
          });
        }}
      />
      <DataNotFoundMessage panelId={state.id} />
      <FormattedMessage error={error} message={message} />
    </PanelLayout>
  );
}

function isViewableInRawMessagePanel(
  topicNodes: TopicNode[],
  topicData: TopicData,
): boolean {
  // If the given message path is itself a binary field, short-circuit further evaluation.
  const dataType = topicData.messagePath.parts.at(-1);
  if (
    dataType?.type === MessagePathPartType.Attr &&
    (dataType.dataType === CanonicalDataType.Image ||
      dataType.dataType === CanonicalDataType.Byte)
  ) {
    return false;
  }

  const topicNode = topicNodes.find((node) => node.id === topicData.topic.id);
  if (!topicNode) {
    // The topic node is not in the topic tree. This would be a programming error.
    throw new Error(`Failed to find topic node for ${topicData.topic.name}`);
  }

  // BFS through all message paths in the topic
  const queue: MessagePathNode[] = [...topicNode.children];
  while (queue.length > 0) {
    const current = queue.shift();
    if (!current) {
      continue;
    }

    // The node being evaluated is bytes-like...
    if (
      current.data.canonical_data_type === CanonicalDataType.Image ||
      current.data.canonical_data_type === CanonicalDataType.Byte
    ) {
      // ...and those bytes are in the same representation file as the target message path's
      if (
        current.findRepresentation()?.representation_id ===
        topicData.representation.id
      ) {
        // ...then we can't load it in the Raw Message panel.
        return false;
      }
    }

    queue.push(...current.children);
  }

  return true;
}
